import { context, propagation, trace, SpanStatusCode, type Span } from '@opentelemetry/api';
import {
  BatchSpanProcessor,
  SimpleSpanProcessor,
  StackContextManager,
  TraceIdRatioBasedSampler,
  TracerProvider,
  type ReadableSpan,
  type SpanExporter,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace';
import {
  CompositePropagator,
  ExportResultCode,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
  type ExportResult,
} from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { logger } from '../logger';

const spanDurationMs = (span: ReadableSpan): number =>
  Math.round((span.endTime[0] - span.startTime[0]) * 1000 + (span.endTime[1] - span.startTime[1]) / 1e6);

const errorConsoleExporter: SpanExporter = {
  export: (spans, resultCallback) => {
    spans
      .filter((span) => span.status.code === SpanStatusCode.ERROR)
      .forEach((span) => console.warn(`[trace] ${span.name} ${spanDurationMs(span)}ms ERROR`, span.status.message));
    resultCallback({ code: ExportResultCode.SUCCESS });
  },
  shutdown: () => Promise.resolve(),
};

const withFailureLogging = (exporter: SpanExporter): SpanExporter => ({
  export: (spans, resultCallback) =>
    exporter.export(spans, (result: ExportResult) => {
      if (result.code !== ExportResultCode.SUCCESS) {
        logger.warn(`[Tracer] Failed to export ${spans.length} spans:`, { code: result.code, error: result.error });
      }
      resultCallback(result);
    }),
  shutdown: () => exporter.shutdown(),
  forceFlush: () => exporter.forceFlush?.() ?? Promise.resolve(),
});

export interface TracerConfig {
  serviceName?: string;
  serviceVersion?: string;
  exporterEndpoint?: string;
  useConsoleExporter?: boolean;
  sampleRate?: number;
}

const state: { provider: TracerProvider | null } = { provider: null };

const createSpanProcessor = (exporterEndpoint: string | undefined, useConsoleExporter: boolean): SpanProcessor =>
  useConsoleExporter || !exporterEndpoint
    ? new SimpleSpanProcessor({ exporter: errorConsoleExporter })
    : new BatchSpanProcessor({
        exporter: withFailureLogging(
          new OTLPTraceExporter({ url: exporterEndpoint, headers: { 'Content-Type': 'application/json' } }),
        ),
        maxQueueSize: 100,
        maxExportBatchSize: 50,
        scheduledDelayMillis: 5000,
        exportTimeoutMillis: 30000,
      });

export function initTracer(config: TracerConfig = {}): () => Promise<void> {
  if (state.provider) {
    logger.warn('[Tracer] Already initialized, skipping...');
    return async () => {};
  }

  const {
    serviceName = 'aircast-web',
    serviceVersion = '1.0.0',
    exporterEndpoint,
    useConsoleExporter = !exporterEndpoint,
    sampleRate = 1.0,
  } = config;

  try {
    const provider = new TracerProvider({
      resource: resourceFromAttributes({
        [ATTR_SERVICE_NAME]: serviceName,
        [ATTR_SERVICE_VERSION]: serviceVersion,
      }),
      sampler: new TraceIdRatioBasedSampler(sampleRate),
      spanProcessors: [createSpanProcessor(exporterEndpoint, useConsoleExporter)],
    });

    context.setGlobalContextManager(new StackContextManager().enable());
    propagation.setGlobalPropagator(
      new CompositePropagator({ propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()] }),
    );
    trace.setGlobalTracerProvider(provider);

    const ignoreUrls = [/\/loki\//, /\/otel\//, /\/v1\/traces/, /faro\./, /localhost:4318/, /localhost:3100/];
    const propagateTraceHeaderCorsUrls = [/aircast\.one/, /localhost/];
    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({ propagateTraceHeaderCorsUrls, ignoreUrls }),
        new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls, ignoreUrls }),
      ],
    });

    state.provider = provider;
    logger.info(
      useConsoleExporter || !exporterEndpoint
        ? `[Tracer] Initialized ${serviceName} v${serviceVersion} with console exporter`
        : `[Tracer] Initialized ${serviceName} v${serviceVersion} with OTLP exporter (BatchSpanProcessor): ${exporterEndpoint}`,
    );

    return async () => {
      await provider.shutdown();
      state.provider = null;
      logger.info('[Tracer] Shutdown complete');
    };
  } catch (error) {
    logger.captureError(error instanceof Error ? error : new Error(String(error)), {
      context: 'Tracer initialization failed',
    });
    return async () => {};
  }
}

export function getTracer() {
  return trace.getTracer('aircast-web');
}

const failSpan = (span: Span, error: unknown) => {
  span.setStatus({ code: SpanStatusCode.ERROR, message: error instanceof Error ? error.message : String(error) });
  span.recordException(error as Error);
};

export async function withSpan<T>(name: string, fn: (span: Span) => Promise<T>): Promise<T> {
  const span = getTracer().startSpan(name);
  return context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const result = await fn(span);
      span.setStatus({ code: SpanStatusCode.OK });
      return result;
    } catch (error) {
      failSpan(span, error);
      throw error;
    } finally {
      span.end();
    }
  });
}

export function withSpanSync<T>(name: string, fn: (span: Span) => T): T {
  const span = getTracer().startSpan(name);
  return context.with(trace.setSpan(context.active(), span), () => {
    try {
      const result = fn(span);
      span.setStatus({ code: SpanStatusCode.OK });
      return result;
    } catch (error) {
      failSpan(span, error);
      throw error;
    } finally {
      span.end();
    }
  });
}

export function getRawTraceId(): string | null {
  return trace.getActiveSpan()?.spanContext().traceId || null;
}

export function recordError(error: Error | unknown, attributes?: Record<string, string>): void {
  const activeSpan = trace.getActiveSpan();
  activeSpan?.recordException(error as Error);
  activeSpan?.setAttributes(attributes ?? {});
}

export function addSpanAttributes(attributes: Record<string, string | number | boolean>): void {
  trace.getActiveSpan()?.setAttributes(attributes);
}
